import { useState, useRef } from "react";
import config from './config.json'

const useApi = () => {
    const [backendUrl] = useState(process.env.REACT_APP_API_URL as string);
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);

    const tokenRef = useRef<string | null>(null);
    tokenRef.current = token;

    const generateHeaders = async (): Promise<Headers> => {

        const headers = new Headers({
            'Content-Type': 'application/json',
        });

        return headers;
    };

    const handleResponse = async (response: any) => {
        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        const data = await response.json();
        return { data };
    };

    const handleStreamedResponse = (response: any) => {
        if (!response.ok) {
            return response.json().then(Promise.reject.bind(Promise));
        }
    
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let content = '';
    
        const stream = new ReadableStream({
            async start(controller) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        break;
                    }
                    content += decoder.decode(value);
                    controller.enqueue(value);
                }
                controller.close();
                reader.releaseLock();
            }
        });
    
        return {
            stream: stream,
            text: () => content,
        };
    };

    const get = async (url: string, options = {}) => {
        return fetch(`${backendUrl}${url}`, {
            method: 'GET',
            headers: await generateHeaders(),
            ...options,
        }).then(handleResponse);
    };

    const getStream = async (url: string, options = {}) => {
        return fetch(`${backendUrl}${url}`, {
            method: 'GET',
            headers: await generateHeaders(),
            ...options,
        }).then(handleStreamedResponse);
    };

    const post = async (url: string, data: any, options = {}) => {

        const headers = await generateHeaders();
        let body;

        if (data instanceof FormData) {
            headers.delete('Content-Type');
            body = data;
        } else if (data instanceof URLSearchParams) {
            headers.set('Content-Type', 'application/x-www-form-urlencoded');
            body = new URLSearchParams(data).toString();
        } else {
            body = JSON.stringify(data);
        }
        
        return fetch(`${backendUrl}${url}`, {
            method: 'POST',
            headers: headers,
            body: body,
            ...options,
        }).then(handleResponse);
    };

    const postStream = async (url: string, data: any, abortSignal?: AbortSignal) => {
        return fetch(`${backendUrl}${url}`, {
            method: 'POST',
            headers: await generateHeaders(),
            body: JSON.stringify(data),
            signal: abortSignal,
        }).then(handleStreamedResponse);
    };

    const put = async (url: string, data: any, options = {}) => {
        const headers = await generateHeaders();
        let body;

        if (data instanceof FormData) {
            headers.delete('Content-Type');
            body = data;
        } else if (data instanceof URLSearchParams) {
            headers.set('Content-Type', 'application/x-www-form-urlencoded');
            body = new URLSearchParams(data).toString();
        } else {
            body = JSON.stringify(data);
        }

        return fetch(`${backendUrl}${url}`, {
            method: 'PUT',
            headers: headers,
            body: body,
            ...options,
        }).then(handleResponse);
    };

    const patch = async (url: string, data: any, options = {}) => {
        const headers = await generateHeaders();
        let body;

        if (data instanceof FormData) {
            headers.delete('Content-Type');
            body = data;
        } else if (data instanceof URLSearchParams) {
            headers.set('Content-Type', 'application/x-www-form-urlencoded');
            body = new URLSearchParams(data).toString();
        } else {
            body = JSON.stringify(data);
        }

        return fetch(`${backendUrl}${url}`, {
            method: 'PATCH',
            headers: headers,
            body: body,
            ...options,
        }).then(handleResponse);
    };

    const remove = async (url: string, options = {}) => {
        return fetch(`${backendUrl}${url}`, {
            method: 'DELETE',
            headers: await generateHeaders(),
            ...options,
        }).then(handleResponse);
    };

    const connectWebSocket = async (url: string) => {

        let wsUrl = `${backendUrl}${url}`;

        if (wsUrl.startsWith('https://')) {
            wsUrl = wsUrl.replace('https://', 'wss://');
        } else if (wsUrl.startsWith('http://')) {
            wsUrl = wsUrl.replace('http://', 'ws://');
        } else {
            wsUrl = `ws://${wsUrl}`;
        }

        const socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            console.log('WebSocket connection established');
        };

        socket.onclose = (event) => {
            console.log('WebSocket connection closed:', event);
        };

        socket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        socket.onmessage = (event) => {
            console.log('WebSocket message received:', event.data);
        };

        return socket;
    };

    return {
        get,
        getStream,
        post,
        postStream,
        put,
        patch,
        remove,
        connectWebSocket,
    };
};

export default useApi;